// Copyright (C) 2021 Bystronic Laser AG
import { AlertOccurrence } from '@/models/alertOccurrence';
import {
  AlertOperator,
  CareAlertMetric,
  WccAlertMetric,
} from '@/models/enums/AlertsEnums';
import i18n from '@/i18n';
import { translateDateGrouping } from '@/utils/dates';
import { alertsService } from '@/services/alerts.service';
import {
  formatWithUnit as formatNumberWithUnit,
  translateUnit,
} from './number';
import { isNil } from './misc';
import { matchIgnoringCase } from '@/utils/table';
import { AlertWorkflow, AlertWorkflowStatus } from '@/models/alertWorkflow';
import { StandardAlert, StandardAlertStatus } from '@/models/standardAlert';
import { CareAlert } from '@/models/careAlert';

export function matchesMetric(
  alert: StandardAlert | CareAlert | AlertOccurrence,
  searchText: string,
): boolean {
  return matchIgnoringCase(translateKpi(alert.metric), searchText);
}

export function matchesOperator(
  alert: StandardAlert | CareAlert | AlertOccurrence,
  searchText: string,
): boolean {
  return matchIgnoringCase(translateOperator(alert.operator), searchText);
}

export function matchesDateGrouping(
  alert: CareAlert | AlertOccurrence,
  searchText: string,
): boolean {
  return matchIgnoringCase(
    translateDateGrouping(alert.dateGrouping!),
    searchText,
  );
}

export function matchesThreshold(
  alert: StandardAlert | CareAlert | AlertOccurrence,
  searchText: string,
): boolean {
  const unit = alertsService.store.getCareAlertMetricUnit(alert.metric);
  return matchIgnoringCase(
    `${alert.threshold} ${translateUnit(unit, alert.threshold)}`,
    searchText,
  );
}

export function matchesVariable(
  alert: CareAlert | AlertOccurrence,
  searchText: string,
): boolean {
  return matchIgnoringCase(
    translateVariable(alert.variable, alert.metric as CareAlertMetric),
    searchText,
  );
}

export function matchesEventSource(
  alert: CareAlert | AlertOccurrence,
  searchText: string,
): boolean {
  return (
    alert.eventSources?.some((source) =>
      matchIgnoringCase(source, searchText),
    ) ?? false
  );
}

export function matchesEventCode(
  alert: CareAlert | AlertOccurrence,
  searchText: string,
): boolean {
  return (
    alert.eventCodes?.some((code) => matchIgnoringCase(code, searchText)) ??
    false
  );
}

export function matchesStatus(
  standardAlert: StandardAlert,
  searchText: string,
): boolean {
  return matchIgnoringCase(translateStatus(standardAlert.status), searchText);
}

export function translateKpi(kpi: WccAlertMetric | CareAlertMetric): string {
  return i18n.t(`alert.metric.${kpi}`).toString();
}

export function translateOperator(operator: AlertOperator): string {
  return i18n.t(`alert_operators.${AlertOperator[operator]}`).toString();
}

export function translateStatus(status: StandardAlertStatus): string {
  return i18n
    .t(`e_service_center.standard_alert_status.${StandardAlertStatus[status]}`)
    .toString();
}

export function translateVariable(
  variable: string | undefined,
  metric: CareAlertMetric,
): string {
  if (isNil(variable)) {
    return '';
  }
  return i18n
    .t(`wizards.create_care_alert.variables.${metric}.${variable}`)
    .toString();
}

export function translateWorkflowStatus(
  workflowStatus: AlertWorkflowStatus,
): string {
  return i18n.t(`alert.workflow_status.${workflowStatus}`).toString();
}

export function formatWithUnit(value: number, metric: CareAlertMetric): string {
  const unit = alertsService.store.getCareAlertMetricUnit(metric);
  return formatNumberWithUnit(value, unit);
}

export function alertMetricSorter(
  a: AlertWorkflow,
  b: AlertWorkflow,
  isAscending: boolean,
): number {
  const aText = translateKpi(a.alertOccurrence.metric);
  const bText = translateKpi(b.alertOccurrence.metric);
  return isAscending ? aText.localeCompare(bText) : bText.localeCompare(aText);
}
